import type { DailyIncidentCommandDto } from "@/features/daily/types/daily.types";

export type IncidentAction = DailyIncidentCommandDto["action"];

export type IncidentCommandIntent = {
    incidentId: string;
    expectedEpisode: number;
    action: IncidentAction;
    assignee: string | null;
};

/**
 * Enmienda C4: the key identifies the INTENT, not the click. Retrying a failed
 * acknowledge must reuse the same key so the server's `create`-based dedup sees a
 * replay instead of a second command; a new episode is a different intent and
 * therefore earns a new key, which is why the episode is part of the identity.
 */
export const intentKey = (intent: IncidentCommandIntent): string =>
    `${intent.incidentId}|${intent.expectedEpisode}|${intent.action}`;

const canonicalPayload = (intent: IncidentCommandIntent): string =>
    JSON.stringify([intent.action, intent.assignee, intent.expectedEpisode, intent.incidentId]);

/**
 * Enmienda C4: the same idempotency key carrying a different payload is a bug, and
 * the server rejects it with 409. The hash is a deterministic FNV-1a over a
 * canonical (order-fixed) serialization of the payload, so the same intent always
 * produces the same token in any runtime.
 */
export const payloadHash = (intent: IncidentCommandIntent): string => {
    const payload = canonicalPayload(intent);
    let hash = 0x811c9dc5;
    for (let index = 0; index < payload.length; index += 1) {
        hash ^= payload.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16).padStart(8, "0");
};

export const buildCommand = (intent: IncidentCommandIntent, idempotencyKey: string): DailyIncidentCommandDto => ({
    incidentId: intent.incidentId,
    expectedEpisode: intent.expectedEpisode,
    action: intent.action,
    assignee: intent.assignee,
    payloadHash: payloadHash(intent),
    idempotencyKey,
});

/**
 * The three 409s the Daily command surface can answer with, anchored to the backend
 * that raises them: `incident_episode_changed` (`daily-access.middleware.ts:164`),
 * `command_keys_full` and `command_identity_mismatch`
 * (`acknowledge-incident.use-case.ts:62,103`). `httpClient` keeps only the `message`
 * of a public 409 (D31's transport), so the token IS what reaches the browser.
 *
 * Each one needs its own words: they ask the operator to do three different things.
 */
export const EPISODE_CONFLICT_COPY = "Esta incidencia cambió mientras la mirabas. Recarga para actuar sobre el episodio actual.";

export const KEYS_FULL_COPY = "Este episodio alcanzó su límite de acciones registradas. Espera a que la incidencia se recupere antes de volver a actuar.";

export const IDENTITY_MISMATCH_COPY = "Este reintento pertenece a otro operador. Recarga para actuar con tu propia orden.";

export const GENERIC_CONFLICT_COPY = "El servidor rechazó la acción porque choca con el estado actual de la incidencia. Recarga antes de volver a actuar.";

const CONFLICT_COPY: Record<string, string> = {
    incident_episode_changed: EPISODE_CONFLICT_COPY,
    command_keys_full: KEYS_FULL_COPY,
    command_identity_mismatch: IDENTITY_MISMATCH_COPY,
};

/**
 * Returns the operator-facing copy for a rejected command, or null when the failure
 * is not one of the known conflicts (a transport error is reported as itself, never
 * dressed up as a state conflict the operator could act on).
 */
export const conflictCopy = (error: { code: string; message: string }): string | null => {
    if (error.code !== "request") return null;
    const known = CONFLICT_COPY[error.message];
    if (known !== undefined) return known;
    // A 409 whose body was not a recognised public error: we know it conflicted, but
    // not with what. Say exactly that rather than guess one of the three above.
    return error.message === "HTTP 409" ? GENERIC_CONFLICT_COPY : null;
};

/**
 * One key per intent, generated once and then reused from component state until the
 * command succeeds (C4). The fallback exists because `randomUUID` is absent in some
 * non-secure contexts; it is only ever used to seed a key, never to prove identity.
 */
const randomBytes = (cryptoApi: Crypto | undefined): number[] => {
    if (cryptoApi !== undefined && typeof cryptoApi.getRandomValues === "function") {
        return Array.from(cryptoApi.getRandomValues(new Uint8Array(16)));
    }
    return Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
};

// D95: the server accepts only a UUID, so the fallback is a v4 UUID too.
const uuidV4Of = (bytes: number[]): string => {
    const shaped = bytes.map((byte, index) => {
        if (index === 6) return (byte & 0x0f) | 0x40;
        if (index === 8) return (byte & 0x3f) | 0x80;
        return byte;
    });
    const hex = shaped.map((byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createIdempotencyKey = (): string => {
    const cryptoApi: Crypto | undefined = globalThis.crypto;
    if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === "function") return cryptoApi.randomUUID();
    return uuidV4Of(randomBytes(cryptoApi));
};
